import { Grid } from '@mui/material';
import { createStyles, WithStyles, withStyles } from '@mui/styles';
import { getInvoicesByEncryptionBusinessIdentifier } from 'lib/api/paymentLink';
import { Invoice } from 'lib/api/paymentLink/types';
import React, { useState } from 'react';
import { useParams } from 'react-router-dom';

import HeaderWithActions from '../../../lib/designSystem/HeaderWithActions';
import Interested from './components/InterestContainer';
import InvoiceRow from './components/InvoiceRow';
import NotifyPayment from './components/NotifyPayment';
import WireInformation from './components/WireInformation';

const styles = createStyles({
  layoutWrapper: {
    display: 'flex',
    height: '100vh', // Covers the entire viewport height
  },
  content: {
    width: '100%',
    flexGrow: 1, // Allows it to grow and consume available space
    display: 'flex', // Added to facilitate flex-based layout
    flexDirection: 'column', // Stack children vertically
  },
  layout: {
    flexGrow: 1, // Allows it to grow and consume available space
    overflowX: 'auto', // Enables vertical scrolling
    padding: '20px',
    whiteSpace: 'nowrap',
    backgroundColor: '#F8FAFC',
    boxSizing: 'border-box',
    height: '100vh',
  },
  side: {
    flexGrow: 1, // Allows it to grow and consume available space
    overflowX: 'auto', // Enables vertical scrolling
    padding: '20px',
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    backgroundColor: '#FFFFFF',
  },
  loading: {
    opacity: 0.6,
    pointerEvents: 'none',
  },
});

interface LayoutProps extends WithStyles<typeof styles> {}

const PaymentLink: React.FC<LayoutProps> = ({ classes }) => {
  const { encryptedCompanyId, encryptedBusinessIdentifier } = useParams<{
    encryptedCompanyId: string;
    encryptedBusinessIdentifier: string;
  }>();
  const { data, isLoading } = getInvoicesByEncryptionBusinessIdentifier(
    encryptedCompanyId,
    encryptedBusinessIdentifier,
  );
  const [openPayment, setOpenPayment] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);

  const handleClickFromInvoice = (invoiceId: string) => {
    if (!data) return;
    setSelectedInvoice(
      data.invoices.find((invoice: Invoice) => invoice.id === invoiceId) ??
        null,
    );
    setOpenPayment(true);
  };

  if (!data) return null;

  const business = data.business;
  const company = data.company;
  const invoices = data.invoices;

  return (
    <div className={classes.layoutWrapper}>
      {openPayment && selectedInvoice && (
        <NotifyPayment
          invoice={selectedInvoice}
          open={openPayment}
          setOpen={setOpenPayment}
        />
      )}
      <div className={classes.content}>
        <Grid container>
          <Grid item xs={12} md={3}>
            {!isLoading && (
              <div
                style={{
                  minWidth: 210,
                  height: '100vh',
                  backgroundColor: 'ffffff',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  borderRight: '1px solid #eaecf0',
                }}
              >
                <div className={classes.side}>
                  <div style={{ textAlign: 'center', padding: '20px' }}>
                    {' '}
                    <img
                      width={175}
                      src={`https://pruff-public.s3.amazonaws.com/company-logos/${company.id}.png`}
                    />
                  </div>
                  <WireInformation
                    encryptedBusinessIdentifier={
                      encryptedBusinessIdentifier || ''
                    }
                    encryptedCompanyId={encryptedCompanyId || ''}
                  />
                </div>
                <Interested companyId={company.id} />
              </div>
            )}
          </Grid>
          <Grid item xs={0} md={9}>
            <div className={classes.layout}>
              <HeaderWithActions
                type="subHeader"
                title={`¡Hola 👋! A continuación encontrarás los documentos emitidos a ${business.name}.`}
                variant="body1"
              />
              {invoices
                .sort(
                  (a: { issuedDate: string }, b: { issuedDate: string }) =>
                    new Date(b.issuedDate).getTime() -
                    new Date(a.issuedDate).getTime(),
                )
                .map((invoice: Invoice, index: number) => (
                  <InvoiceRow
                    key={index}
                    handleClickFromInvoice={handleClickFromInvoice}
                    invoice={invoice}
                  />
                ))}
            </div>
          </Grid>
        </Grid>
      </div>
    </div>
  );
};

export default withStyles(styles)(PaymentLink);
